import { IDecodedToken } from 'api/interfaces/auth';
import { jwtDecode } from 'jwt-decode';
import { storageKeys } from './constants';
import moment from 'moment';
import { DictMeasurementUnit, DictVolumeUnit } from '../../../../libs/common/src/Shared/Enums';
import { ILockerDetailsInfo, IRentalDetails } from 'api/interfaces/lockerGate';

export const clearStorage = (keys?: string[], storageType?: 'local' | 'session') => {
	if (!keys && !storageType) {
		localStorage.clear();
		sessionStorage.clear();
		return;
	} else if (keys) {
		keys.map((key) => {
			if (!storageType || storageType === 'session') {
				sessionStorage.removeItem(key);
			}
			if (!storageType || storageType === 'local') {
				localStorage.removeItem(key);
			}
		});
	}
};

export const getUserDataFromAccessToken = (accessTokenData?: string) => {
	const accessToken =
		accessTokenData ??
		(sessionStorage.getItem(storageKeys.ACCESS_TOKEN) || localStorage.getItem(storageKeys.ACCESS_TOKEN));

	if (accessToken) {
		const decodedToken = jwtDecode<IDecodedToken>(accessToken);

		return {
			id: decodedToken.nameid,
			userName: decodedToken.unique_name,
			name: decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/userdata'],
			userWorkplaceId: decodedToken.userWorkplaceId,
			userWorkplaceName: decodedToken.userWorkplaceName,
			roles: decodedToken.role,
			allLocationsHasTheSameProducts: decodedToken.allLocationsHasTheSameProducts === 'true',
			apkVersion: decodedToken.APK_VERSION,
		};
	}
	return null;
};

export const formatDateTime = (str: string) => {
	return moment(str).local().format('DD.MM.YYYY HH:mm');
};
export const formatDateTimeWithSeconds = (str: string) => {
	return moment(str).local().format('DD.MM.YYYY HH:mm:ss');
};
export const formatDate = (str: string) => {
	return moment(str).local().format('DD.MM.YYYY');
};

export const formatPhoneNumber = (phone: string, phoneCountryCode: number): string => {
	if (!phone || phone.trim() === '') return '';
	const cleaned = phone.replace(/\D/g, '');
	if (cleaned.length === 9) {
		return `+${phoneCountryCode} ${cleaned.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}`;
	} else {
		return `+${phoneCountryCode} ${cleaned}`;
	}
};

const measurementUnitLabelMap = new Map<DictMeasurementUnit, string>([
	[DictMeasurementUnit.m, 'm'],
	[DictMeasurementUnit.cm, 'cm'],
]);

const getDimensionsString = (lockerSize: ILockerDetailsInfo | undefined, noBreak?: boolean) => {
	if (lockerSize && (lockerSize.length || lockerSize.height || lockerSize.width)) {
		const space = noBreak ? '\u00A0' : ' ';
		let length = lockerSize.length || '';
		let width = lockerSize.width && length ? ` x ${lockerSize.width}` : lockerSize.width || '';
		let height = lockerSize.height && (length || width) ? ` x ${lockerSize.height}` : lockerSize.height || '';
		let unitString = measurementUnitLabelMap.get(lockerSize.dictMeasurementType ?? DictMeasurementUnit.m);

		return `${length}${width}${height}${space}${unitString}`;
	} else return '';
};
function DictVolumeUnitLabel(): Map<number, string> {
	return new Map<number, string>([
		[DictVolumeUnit.m3, 'm³'],
		[DictVolumeUnit.m2, 'm²'],
	]);
}
const getAreaOrVolume = (condition: boolean, unit: DictVolumeUnit, value?: string | number | null) => {
	if (value && Number(value)) {
		return `${condition ? '\u00A0| ' : ''}${parseAndFormatPrice(Number(value))}\u00A0${DictVolumeUnitLabel().get(unit)}`;
	} else return '';
};
export const parseAndFormatPrice = (
	price: number | undefined,
	decimalPoints: number = 2,
	divideByCents: boolean = true,
	locale?: string
) => {
	const parsedPrice = Number(price);
	if (!isNaN(parsedPrice)) {
		const value = parsedPrice / (divideByCents ? 100 : 1);
		if (locale) {
			return new Intl.NumberFormat(locale, {
				minimumFractionDigits: decimalPoints,
				maximumFractionDigits: decimalPoints,
			}).format(value);
		}
		return value.toFixed(decimalPoints).replace('.', ',');
	}
	return '';
};
export const getLockerDimensions = (size: ILockerDetailsInfo | null | undefined): string => {
	let dimensions = getDimensionsString(size!, true);
	return `${dimensions ? dimensions : ''}${getAreaOrVolume(!!dimensions, DictVolumeUnit.m2, size?.area)}${getAreaOrVolume(!!(dimensions || size?.area), DictVolumeUnit.m3, size?.volume)}`;
};

export const getRemainingDaysString = (paidToRemainingTime: number, t: (key: string) => void): string => {
	return paidToRemainingTime > 1 || paidToRemainingTime < -1 || paidToRemainingTime === 0
		? `${paidToRemainingTime}  ${t('common.days')}`
		: paidToRemainingTime === 1 || paidToRemainingTime === -1
		? `${paidToRemainingTime}  ${t('common.day')}`
		: '';
};

export const getOrderPayment = (rowData: IRentalDetails, t: (key: string) => void): string => {
	return rowData.isPaid !== null
		? rowData.isPaid && rowData.paidTo
			? t('common.to') + ' ' + formatDate(rowData.paidTo)
			: rowData.isPaid === false
			? `${t('common.unpaid')} ${
					rowData.paidToRemainingDays !== null ? `(${getRemainingDaysString(rowData.paidToRemainingDays, t)})` : ''
			  }`
			: ''
		: 'n/a';
};
